import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useAuth, hasRole } from '@/lib/auth';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from '@/components/ui/alert-dialog';
import SupabaseAuthUsers from '@/components/SupabaseAuthUsers';
import { Database as DBIcon, FileText, HardDrive, KeyRound, Cpu, ChevronLeft, ChevronRight, Eye, Activity, Server, Clock, Plus, Pencil, Trash2, AlertTriangle } from 'lucide-react';

function fmtBytes(n) {
  if (!n) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB'];
  let i = 0; let v = n;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(1)} ${u[i]}`;
}

function fmtUptime(s) {
  if (!s) return '—';
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d) return `${d}d ${h}h`;
  if (h) return `${h}h ${m}m`;
  return `${m}m`;
}

function StatCard({ icon: Icon, label, value, sub, accent }) {
  return (
    <Card className="p-4 bg-card border border-border">
      <div className="flex justify-between items-center">
        <div className="text-[10px] uppercase tracking-wide text-[hsl(var(--muted-foreground))]">{label}</div>
        <Icon className={`h-4 w-4 ${accent || 'text-[hsl(var(--primary))]'}`} />
      </div>
      <div className="text-2xl font-display font-semibold tabular-nums mt-2">{value}</div>
      {sub && <div className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1">{sub}</div>}
    </Card>
  );
}

function MongoMonitor() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const canExplore = hasRole(user, ['admin', 'tech']);
  const [stats, setStats] = useState(null);
  const [collection, setCollection] = useState(null);
  const [docs, setDocs] = useState({ documents: [], total: 0, page: 1, page_size: 25 });
  const [selected, setSelected] = useState(null);
  const [page, setPage] = useState(1);

  const load = async () => { const { data } = await api.get('/database/stats'); setStats(data); };
  useEffect(() => { load(); const id = setInterval(load, 20000); return () => clearInterval(id); }, []);

  const openCollection = async (name, p = 1) => {
    setCollection(name); setPage(p);
    const { data } = await api.get(`/database/collections/${name}`, { params: { page: p, page_size: 25 } });
    setDocs(data);
  };

  if (!stats) return <div className="text-sm text-[hsl(var(--muted-foreground))]">{t('loading')}</div>;
  const totalPages = Math.max(1, Math.ceil((docs.total || 0) / (docs.page_size || 25)));

  return (
    <div className="space-y-4">
      <div className="text-xs text-[hsl(var(--muted-foreground))] font-mono">{stats.db_name} • MongoDB {stats.server?.version || ''} (BOMM internal)</div>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard icon={DBIcon} label="Status" value={stats.ok ? 'Healthy' : 'Down'} accent={stats.ok ? 'text-emerald-400' : 'text-red-400'} />
        <StatCard icon={FileText} label={t('database_collections')} value={stats.collections.length} />
        <StatCard icon={Cpu} label={t('database_objects')} value={stats.objects} />
        <StatCard icon={HardDrive} label={t('database_size')} value={fmtBytes(stats.storage_size_bytes)} />
        <StatCard icon={KeyRound} label={t('database_indexes')} value={stats.indexes} />
      </div>
      <Card className="p-5 bg-card border border-border">
        <h2 className="font-display text-base font-semibold mb-3">{t('database_collections')}</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {stats.collections.map((c) => (
            <button key={c.name} disabled={!canExplore} onClick={() => openCollection(c.name)}
              className="text-left p-3 rounded-lg border border-border bg-[hsl(var(--muted))] hover:border-[hsl(var(--primary)/.4)] disabled:opacity-60 transition-colors duration-150"
              data-testid={`db-collection-item-${c.name}`}>
              <div className="text-sm font-medium">{c.name}</div>
              <div className="text-xs text-[hsl(var(--muted-foreground))] font-mono mt-1">{c.count} docs</div>
            </button>
          ))}
        </div>
      </Card>
      {collection && (
        <Card className="bg-card border border-border">
          <div className="p-4 flex items-center justify-between border-b border-border">
            <div>
              <div className="font-display text-base font-semibold">{collection}</div>
              <div className="text-xs text-[hsl(var(--muted-foreground))] font-mono">{t('database_explorer')} • {docs.total} docs</div>
            </div>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => openCollection(collection, page - 1)}><ChevronLeft className="h-4 w-4" /></Button>
              <span className="text-xs font-mono">{page} / {totalPages}</span>
              <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => openCollection(collection, page + 1)}><ChevronRight className="h-4 w-4" /></Button>
            </div>
          </div>
          <div className="max-h-[420px] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-card">
                <tr className="text-left text-[10px] uppercase tracking-wide text-[hsl(var(--muted-foreground))] border-b border-border"><th className="px-3 py-2">id</th><th className="px-3 py-2">preview</th><th className="px-3 py-2"></th></tr>
              </thead>
              <tbody className="font-mono">
                {docs.documents.map((d, i) => (
                  <tr key={i} className="border-b border-border/40 hover:bg-accent/40">
                    <td className="px-3 py-2 text-[hsl(var(--muted-foreground))]">{d.id || i}</td>
                    <td className="px-3 py-2 truncate max-w-[600px]">{JSON.stringify(d).slice(0, 140)}</td>
                    <td className="px-3 py-2 text-right"><Button variant="ghost" size="sm" onClick={() => setSelected(d)}><Eye className="h-3.5 w-3.5" /></Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
      <Sheet open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <SheetContent className="w-[560px] sm:max-w-[560px] overflow-y-auto">
          <SheetHeader><SheetTitle>Document</SheetTitle></SheetHeader>
          <pre className="mt-4 text-xs font-mono whitespace-pre-wrap break-words bg-[hsl(var(--muted))] p-3 rounded-lg border border-border">{selected ? JSON.stringify(selected, null, 2) : ''}</pre>
        </SheetContent>
      </Sheet>
    </div>
  );
}

function SupabaseMonitor() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const canExplore = hasRole(user, ['admin', 'tech']);
  const isAdmin = hasRole(user, ['admin']);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const [tableName, setTableName] = useState(null);
  const [tableData, setTableData] = useState(null);
  const [schema, setSchema] = useState(null);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  // editor: { mode: 'create'|'edit', row }
  const [editor, setEditor] = useState(null);
  const [form, setForm] = useState({});
  const [original, setOriginal] = useState({});
  const [saving, setSaving] = useState(false);
  // confirm: { type: 'save'|'delete', row }
  const [confirm, setConfirm] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get('/supabase/stats');
      setStats(data); setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Connection failed');
    }
  };
  useEffect(() => { load(); const id = setInterval(load, 20000); return () => clearInterval(id); }, []);

  const openTable = async (name, p = 1) => {
    setTableName(name); setPage(p);
    try {
      const [{ data }, sc] = await Promise.all([
        api.get(`/supabase/tables/${name}`, { params: { page: p, page_size: 25 } }),
        api.get(`/supabase/tables/${name}/schema`).then((r) => r.data).catch(() => null),
      ]);
      setTableData(data);
      if (sc) setSchema(sc);
    } catch (e) {
      setTableData(null);
    }
  };

  const toStr = (v) => (v === null || v === undefined) ? '' : (typeof v === 'object' ? JSON.stringify(v) : String(v));

  const openCreate = () => {
    const init = {};
    (schema?.columns || []).forEach((c) => { init[c.name] = ''; });
    setForm(init); setOriginal({}); setEditor({ mode: 'create', row: null });
  };

  const openEdit = (row) => {
    const init = {};
    (schema?.columns || []).forEach((c) => { init[c.name] = toStr(row[c.name]); });
    setForm(init); setOriginal({ ...init }); setEditor({ mode: 'edit', row });
  };

  const pkFromRow = (row) => {
    const pk = {};
    (schema?.primary_keys || []).forEach((k) => { pk[k] = row[k]; });
    return pk;
  };

  const requestSave = () => setConfirm({ type: 'save', row: editor.row });
  const requestDelete = (row) => setConfirm({ type: 'delete', row });

  const doSave = async () => {
    setSaving(true);
    try {
      if (editor.mode === 'create') {
        const values = {};
        Object.entries(form).forEach(([k, v]) => { if (v !== '') values[k] = v; });
        await api.post(`/supabase/tables/${tableName}/rows`, { values });
        toast.success('Fila creada correctamente');
      } else {
        const pkCols = schema?.primary_keys || [];
        const values = {};
        Object.entries(form).forEach(([k, v]) => {
          if (!pkCols.includes(k) && v !== original[k]) values[k] = v;
        });
        if (Object.keys(values).length === 0) { toast.info('No hay cambios para guardar'); setConfirm(null); setSaving(false); return; }
        await api.put(`/supabase/tables/${tableName}/rows`, { pk: pkFromRow(editor.row), values });
        toast.success('Fila actualizada correctamente');
      }
      setConfirm(null); setEditor(null);
      await openTable(tableName, page);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    setSaving(true);
    try {
      await api.delete(`/supabase/tables/${tableName}/rows`, { data: { pk: pkFromRow(confirm.row) } });
      toast.success('Fila eliminada');
      setConfirm(null); setSelected(null);
      await openTable(tableName, page);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al eliminar');
    } finally {
      setSaving(false);
    }
  };

  const hasPk = (schema?.primary_keys || []).length > 0;

  if (error) return (
    <Card className="p-6 bg-card border border-red-500/30 text-red-300" data-testid="supabase-error">
      <div className="font-semibold mb-1">Supabase unavailable</div>
      <div className="text-xs font-mono">{error}</div>
    </Card>
  );
  if (!stats) return <div className="text-sm text-[hsl(var(--muted-foreground))]">{t('loading')}</div>;

  const totalPages = tableData ? Math.max(1, Math.ceil((tableData.total || 0) / (tableData.page_size || 25))) : 1;

  return (
    <div className="space-y-4" data-testid="supabase-monitor">
      <div className="flex items-center gap-2 text-xs text-[hsl(var(--muted-foreground))] font-mono">
        <span className="text-[hsl(var(--primary))]">●</span>
        <span>{stats.db_name}</span>
        <span>•</span>
        <span>Postgres {stats.version}</span>
        <span>•</span>
        <span>Supabase</span>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard icon={Activity} label="Status" value={stats.ok ? 'Healthy' : 'Down'} accent={stats.ok ? 'text-emerald-400' : 'text-red-400'} sub={`Postgres ${stats.version}`} />
        <StatCard icon={Server} label="Connections" value={`${stats.connections}/${stats.max_connections}`} sub={`${Math.round(stats.connections / stats.max_connections * 100)}% used`} />
        <StatCard icon={HardDrive} label={t('database_size')} value={fmtBytes(stats.size_bytes)} />
        <StatCard icon={FileText} label="Tables (public)" value={stats.tables.length} sub={`${stats.schemas.length} schemas`} />
        <StatCard icon={Clock} label="Uptime" value={fmtUptime(stats.uptime_s)} />
      </div>

      <Card className="p-5 bg-card border border-border">
        <h2 className="font-display text-base font-semibold mb-3">Schemas</h2>
        <div className="flex flex-wrap gap-2">
          {stats.schemas.map((s) => (
            <span key={s} className="px-2.5 py-1 rounded-full text-xs border border-border bg-[hsl(var(--muted))] font-mono">{s}</span>
          ))}
        </div>
      </Card>

      <Card className="p-5 bg-card border border-border">
        <h2 className="font-display text-base font-semibold mb-3">Tablas (schema public)</h2>
        {stats.tables.length === 0 ? (
          <div className="text-sm text-[hsl(var(--muted-foreground))] py-6 text-center">
            No hay tablas en el schema <span className="font-mono">public</span> aún.<br/>
            <span className="text-xs">Las tablas de Supabase Auth viven en el schema <span className="font-mono">auth</span> (no se exponen aquí por seguridad).</span>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {stats.tables.map((tb) => (
              <button key={tb.name} disabled={!canExplore} onClick={() => openTable(tb.name)}
                className="text-left p-3 rounded-lg border border-border bg-[hsl(var(--muted))] hover:border-[hsl(var(--primary)/.4)] disabled:opacity-60 transition-colors duration-150"
                data-testid={`supabase-table-item-${tb.name}`}>
                <div className="text-sm font-medium">{tb.name}</div>
                <div className="text-xs text-[hsl(var(--muted-foreground))] font-mono mt-1">~{tb.estimated_rows} rows • {fmtBytes(tb.total_bytes)}</div>
              </button>
            ))}
          </div>
        )}
      </Card>

      {tableName && tableData && (
        <Card className="bg-card border border-border" data-testid="supabase-table-explorer">
          <div className="p-4 flex items-center justify-between border-b border-border">
            <div>
              <div className="font-display text-base font-semibold">{tableName}</div>
              <div className="text-xs text-[hsl(var(--muted-foreground))] font-mono">{t('database_explorer')} • {tableData.total} rows • {tableData.columns.length} columns{hasPk ? '' : ' • sin PK'}</div>
            </div>
            <div className="flex items-center gap-2">
              {isAdmin && (
                <Button variant="outline" size="sm" onClick={openCreate} data-testid="supabase-new-row-btn">
                  <Plus className="h-3.5 w-3.5 mr-1" /> Nueva fila
                </Button>
              )}
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => openTable(tableName, page - 1)}><ChevronLeft className="h-4 w-4" /></Button>
                <span className="text-xs font-mono">{page} / {totalPages}</span>
                <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => openTable(tableName, page + 1)}><ChevronRight className="h-4 w-4" /></Button>
              </div>
            </div>
          </div>
          {isAdmin && !hasPk && (
            <div className="px-4 py-2 text-[11px] text-amber-400 bg-amber-500/10 border-b border-amber-500/20 flex items-center gap-2">
              <AlertTriangle className="h-3.5 w-3.5" /> Esta tabla no tiene clave primaria; no se puede editar ni eliminar filas de forma segura.
            </div>
          )}
          <div className="max-h-[420px] overflow-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-card">
                <tr className="text-left text-[10px] uppercase tracking-wide text-[hsl(var(--muted-foreground))] border-b border-border">
                  {tableData.columns.slice(0, 6).map((c) => <th key={c.name} className="px-3 py-2">{c.name}<span className="ml-1 normal-case text-[9px] opacity-60">{c.type}</span></th>)}
                  <th className="px-3 py-2 text-right">···</th>
                </tr>
              </thead>
              <tbody className="font-mono">
                {tableData.documents.map((d, i) => (
                  <tr key={i} className="border-b border-border/40 hover:bg-accent/40" data-testid={`supabase-row-${i}`}>
                    {tableData.columns.slice(0, 6).map((c) => (
                      <td key={c.name} className="px-3 py-2 truncate max-w-[200px]">{d[c.name] === null || d[c.name] === undefined ? <span className="text-[hsl(var(--muted-foreground))]">null</span> : String(d[c.name]).slice(0, 80)}</td>
                    ))}
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <Button variant="ghost" size="sm" onClick={() => setSelected(d)} data-testid={`supabase-view-${i}`}><Eye className="h-3.5 w-3.5" /></Button>
                      {isAdmin && hasPk && (
                        <>
                          <Button variant="ghost" size="sm" onClick={() => openEdit(d)} data-testid={`supabase-edit-${i}`}><Pencil className="h-3.5 w-3.5" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => requestDelete(d)} data-testid={`supabase-delete-${i}`}><Trash2 className="h-3.5 w-3.5 text-red-400" /></Button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
                {tableData.documents.length === 0 && <tr><td colSpan={tableData.columns.length + 1} className="px-4 py-8 text-center text-[hsl(var(--muted-foreground))] font-sans">{t('no_data')}</td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Sheet open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <SheetContent className="w-[560px] sm:max-w-[560px] overflow-y-auto" data-testid="supabase-row-drawer">
          <SheetHeader><SheetTitle>Row</SheetTitle></SheetHeader>
          <pre className="mt-4 text-xs font-mono whitespace-pre-wrap break-words bg-[hsl(var(--muted))] p-3 rounded-lg border border-border">{selected ? JSON.stringify(selected, null, 2) : ''}</pre>
        </SheetContent>
      </Sheet>

      {/* Row editor (create/edit) */}
      <Dialog open={!!editor} onOpenChange={(v) => !v && setEditor(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="supabase-editor-dialog">
          <DialogHeader>
            <DialogTitle>{editor?.mode === 'create' ? `Nueva fila en ${tableName}` : `Editar fila en ${tableName}`}</DialogTitle>
            <DialogDescription>
              {editor?.mode === 'create' ? 'Deja un campo vacío para usar su valor por defecto.' : 'Las claves primarias no se pueden modificar.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {(schema?.columns || []).map((c) => {
              const isPk = (schema?.primary_keys || []).includes(c.name);
              const isLong = ['json', 'jsonb', 'text'].includes(c.type);
              return (
                <div key={c.name} className="space-y-1">
                  <Label className="text-xs flex items-center gap-2">
                    {c.name}
                    <span className="text-[9px] font-mono text-[hsl(var(--muted-foreground))]">{c.type}</span>
                    {isPk && <span className="text-[9px] px-1 rounded bg-[hsl(var(--primary)/.2)] text-[hsl(var(--primary))]">PK</span>}
                    {!c.nullable && !isPk && <span className="text-[9px] text-amber-400">requerido</span>}
                  </Label>
                  {isLong ? (
                    <Textarea
                      value={form[c.name] ?? ''}
                      disabled={isPk && editor?.mode === 'edit'}
                      onChange={(e) => setForm((f) => ({ ...f, [c.name]: e.target.value }))}
                      className="font-mono text-xs min-h-[70px]"
                      data-testid={`supabase-field-${c.name}`}
                    />
                  ) : (
                    <Input
                      value={form[c.name] ?? ''}
                      disabled={isPk && editor?.mode === 'edit'}
                      onChange={(e) => setForm((f) => ({ ...f, [c.name]: e.target.value }))}
                      className="font-mono text-xs"
                      data-testid={`supabase-field-${c.name}`}
                    />
                  )}
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditor(null)} data-testid="supabase-editor-cancel">Cancelar</Button>
            <Button onClick={requestSave} data-testid="supabase-editor-save">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation (always) */}
      <AlertDialog open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}>
        <AlertDialogContent data-testid="supabase-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm?.type === 'delete' ? '¿Eliminar esta fila?' : '¿Confirmar los cambios?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm?.type === 'delete'
                ? `Se eliminará permanentemente la fila de la tabla "${tableName}" en la base de datos de Tu Iglesia. Esta acción no se puede deshacer.`
                : `Se aplicarán los cambios directamente en la tabla "${tableName}" de la base de datos de producción.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="supabase-confirm-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              disabled={saving}
              onClick={(e) => { e.preventDefault(); confirm?.type === 'delete' ? doDelete() : doSave(); }}
              className={confirm?.type === 'delete' ? 'bg-red-600 hover:bg-red-700' : ''}
              data-testid="supabase-confirm-accept"
            >
              {saving ? 'Procesando…' : (confirm?.type === 'delete' ? 'Eliminar' : 'Confirmar')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default function DatabasePage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAdmin = hasRole(user, ['admin']);
  return (
    <div className="space-y-6" data-testid="database-page">
      <div>
        <h1 className="font-display text-2xl font-semibold">{t('database_title')}</h1>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">BD Principal (Supabase) y BOMM Internal (MongoDB)</p>
      </div>
      <Tabs defaultValue="supabase" className="w-full">
        <TabsList>
          <TabsTrigger value="supabase" data-testid="tab-supabase">BD Principal · Supabase</TabsTrigger>
          {isAdmin && <TabsTrigger value="authusers" data-testid="tab-authusers">Usuarios App · Auth</TabsTrigger>}
          <TabsTrigger value="mongo" data-testid="tab-mongo">BOMM Internal · MongoDB</TabsTrigger>
        </TabsList>
        <TabsContent value="supabase" className="mt-4"><SupabaseMonitor /></TabsContent>
        {isAdmin && <TabsContent value="authusers" className="mt-4"><SupabaseAuthUsers /></TabsContent>}
        <TabsContent value="mongo" className="mt-4"><MongoMonitor /></TabsContent>
      </Tabs>
    </div>
  );
}
